import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, setDoc, updateDoc } from 'firebase/firestore';

const Register = () => {
  const navigate = useNavigate();
  const { signup, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    phone: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 表单验证
    if (formData.password !== formData.confirmPassword) {
      return setError('两次输入的密码不一致');
    }
    
    if (formData.password.length < 6) {
      return setError('密码长度至少为6位');
    }

    try {
      setError('');
      setLoading(true);
      
      // 创建用户并保存到Firestore (让AuthContext的signup方法处理)
      const user = await signup(formData.email, formData.password, formData.displayName);
      
      // 在需要时更新额外信息
      if (formData.phone) {
        const db = getFirestore();
        await updateDoc(doc(db, 'users', user.uid), {
          phone: formData.phone
        });
      }

      // 注册成功后跳转到首页
      navigate('/');
    } catch (err) {
      console.error('注册错误:', err);
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('该邮箱已被注册');
          break;
        case 'auth/invalid-email':
          setError('邮箱格式不正确');
          break;
        case 'auth/operation-not-allowed':
          setError('注册功能暂时不可用');
          break;
        case 'auth/weak-password':
          setError('密码强度太弱');
          break;
        default:
          setError('注册失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      navigate('/');
    } catch (err) {
      console.error('Google注册错误:', err);
      setError('Google注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            注册账号
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="用户名"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              autoComplete="name"
              autoFocus
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              label="邮箱"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="email"
            />

            <TextField
              margin="normal"
              fullWidth
              label="手机号码"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              autoComplete="tel"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="密码"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="确认密码"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              autoComplete="new-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : '注册'}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>或</Divider>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignup}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            使用Google账号注册
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              已有账号？
              <Link to="/login" style={{ textDecoration: 'none', marginLeft: '4px' }}>
                立即登录
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;